import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radar, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/auth/forgot-password`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true }
      );
      setSubmitted(true);
    } catch (err) {
      // Rate-limit (429) is the one non-200 we surface to the user.
      // Any other error we still show a generic message — never leak whether
      // the email exists.
      const status = err?.response?.status;
      if (status === 429) {
        setError(
          formatApiErrorDetail(err?.response?.data?.detail) ||
            "Too many reset requests. Please wait an hour and try again."
        );
      } else {
        setSubmitted(true); // enumeration-safe fallback
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
    >
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-5 w-5 text-[#3b82f6]" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#f8fafc]">
              SIGNAL EDGE
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
              convergence intel
            </div>
          </div>
        </Link>

        <div className="border border-[#1e293b] bg-[#111827] p-6">
          {submitted ? (
            <div data-testid="forgot-password-submitted">
              <div className="mb-3 flex items-center gap-2 text-[#22c55e]">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  Request received
                </span>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">
                Check your inbox
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-[#94a3b8]">
                If an account exists for <strong className="text-[#e2e8f0]">{email}</strong>,
                we've sent a reset link. Follow the link within 45 minutes to
                set a new password.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-[#64748b]">
                Didn't receive it? Check spam, or try again in an hour if the
                inbox is slow.
              </p>
              <Link
                to="/login"
                data-testid="forgot-password-back-to-login"
                className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-[#60a5fa] hover:text-[#3b82f6]"
              >
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
                Password reset
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">
                Forgot your password?
              </h1>
              <p className="mt-1 text-sm text-[#94a3b8]">
                Enter your email — we'll send you a link to reset it.
              </p>

              <form onSubmit={onSubmit} className="mt-5 space-y-3">
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                  <input
                    type="email"
                    autoFocus
                    required
                    autoComplete="email"
                    data-testid="forgot-password-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={loading}
                    className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6] disabled:opacity-60"
                  />
                </div>

                {error && (
                  <div
                    data-testid="forgot-password-error"
                    className="border border-[#dc2626]/40 bg-[#dc2626]/10 p-2.5 font-mono text-[11px] text-[#f87171]"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit-button"
                  className="flex w-full items-center justify-center gap-2 bg-[#3b82f6] py-2.5 text-sm font-semibold text-white transition hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </button>
              </form>

              <div className="mt-5 border-t border-[#1e293b] pt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b]">
                Remembered it?{" "}
                <Link
                  to="/login"
                  data-testid="forgot-password-login-link"
                  className="text-[#60a5fa] hover:text-[#3b82f6]"
                >
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
